import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Block, type Stream, type User } from './api/client.ts';
import { useExclusiveAudio } from './lib/audio-solo.ts';
import { useBlocks } from './lib/use-blocks.ts';
import { t } from './i18n/index.ts';
import { Login } from './components/Login.tsx';
import { StreamCard } from './components/StreamCard.tsx';
import { BlockGrid } from './components/BlockGrid.tsx';
import { LiveButton } from './components/LiveButton.tsx';
import { ExportsTable } from './components/ExportsTable.tsx';
import { AdminScreen } from './components/AdminScreen.tsx';
import { TranscribeScreen } from './components/TranscribeScreen.tsx';
import { HelpGuide } from './components/HelpGuide.tsx';
import { VideoScreen } from './components/VideoScreen.tsx';

type AuthState = { status: 'checking' } | { status: 'out' } | { status: 'in'; user: User };

export function App(): React.ReactElement {
  const [auth, setAuth] = useState<AuthState>({ status: 'checking' });
  const [streams, setStreams] = useState<Stream[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  /**
   * The half hour open in the editor. The editor is also the only player:
   * listening in one place and cutting in another made the producer find the
   * same moment twice.
   */
  const [editing, setEditing] = useState<Block | null>(null);
  const [liveOn, setLiveOn] = useState(false);
  const [view, setView] = useState<'archive' | 'exports' | 'transcribe' | 'video' | 'admin'>('archive');

  // Belt and braces for the rule below: whatever starts, everything else stops.
  useExclusiveAudio();

  const checkSession = useCallback(async (): Promise<void> => {
    try {
      const user = await api.me();
      setAuth({ status: 'in', user });
    } catch (err) {
      // Only a 401 means signed out; a network blip must not throw the producer
      // back to the sign-in screen mid-shift.
      if (err instanceof ApiError && err.status === 401) setAuth({ status: 'out' });
      else setAuth((prev) => (prev.status === 'in' ? prev : { status: 'out' }));
    }
  }, []);

  useEffect(() => { void checkSession(); }, [checkSession]);

  const loadStreams = useCallback((): void => {
    void api.streams().then((list) => {
      setStreams(list);
      setSelectedId((current) => current ?? list[0]?.id ?? null);
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (auth.status !== 'in') return;
    loadStreams();
  }, [auth.status, loadStreams]);

  const { blocks, failed: blocksFailed } = useBlocks(selectedId);

  if (auth.status === 'checking') {
    return <div className="empty">{t('player.loading')}</div>;
  }

  if (auth.status === 'out') {
    return <Login onSignedIn={() => void checkSession()} />;
  }

  const timeZone = auth.user.timezone;
  const selected = streams.find((s) => s.id === selectedId) ?? null;

  async function signOut(): Promise<void> {
    await api.logout().catch(() => undefined);
    setEditing(null);
    setLiveOn(false);
    setAuth({ status: 'out' });
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">{t('app.title')}</span>
        <LiveButton
          stream={selected}
          playing={liveOn}
          onToggle={(next) => {
            // Only one thing makes sound at a time.
            if (next) setEditing(null);
            setLiveOn(next);
          }}
        />
        <nav className="tabs">
          <button
            type="button"
            className={view === 'archive' ? 'selected' : ''}
            onClick={() => setView('archive')}
          >
            {t('nav.streams')}
          </button>
          <button
            type="button"
            className={view === 'exports' ? 'selected' : ''}
            onClick={() => setView('exports')}
          >
            {t('nav.exports')}
          </button>
          <button
            type="button"
            className={view === 'transcribe' ? 'selected' : ''}
            onClick={() => setView('transcribe')}
          >
            {t('nav.transcribe')}
          </button>
          <button
            type="button"
            className={view === 'video' ? 'selected' : ''}
            onClick={() => setView('video')}
          >
            {t('nav.video')}
          </button>
          {/* Only an admin may manage streams, people and retention (§7). */}
          {auth.user.role === 'admin' && (
            <button
              type="button"
              className={view === 'admin' ? 'selected' : ''}
              onClick={() => setView('admin')}
            >
              {t('nav.admin')}
            </button>
          )}
        </nav>
        <span className="spacer" />
        <span className="wordmark">ASHAMS</span>
        <span className="who">{auth.user.display_name}</span>
        <button type="button" onClick={() => void signOut()}>{t('auth.logout')}</button>
      </header>

      {/* Rendered once a stream is known, so the guide never has to invent
          the archive window or show a placeholder in place of it. */}
      {(selected ?? streams[0]) && (
        <HelpGuide retentionHours={(selected ?? streams[0])!.retention_hours} />
      )}

      <main className="content">
        {view === 'exports' && <ExportsTable timeZone={timeZone} />}

        {view === 'transcribe' && <TranscribeScreen timeZone={timeZone} />}

        {view === 'video' && <VideoScreen timeZone={timeZone} />}

        {view === 'admin' && auth.user.role === 'admin' && (
          <AdminScreen timeZone={timeZone} streams={streams} onStreamsChanged={loadStreams} />
        )}

        {view === 'archive' && (
        <>
        {streams.length === 0 && <div className="empty">{t('empty.no_streams')}</div>}
        {/* Choosing a stream only means something when there is more than one.
            With a single stream the card was a row of engineering figures
            pushing the recordings down the page. */}
        {streams.length > 1 && (
          <div className="streams">
            {streams.map((stream) => (
              <StreamCard
                key={stream.id}
                stream={stream}
                timeZone={timeZone}
                selected={stream.id === selectedId}
                onSelect={() => setSelectedId(stream.id)}
              />
            ))}
          </div>
        )}

        {selected && (
          <BlockGrid
            stream={selected}
            timeZone={timeZone}
            blocks={blocks}
            loadFailed={blocksFailed}
            editingBlockStart={editing?.block_start_at ?? null}
            onCloseEditor={() => setEditing(null)}
            onEdit={(block) => {
              // Only one thing makes sound at a time.
              setLiveOn(false);
              setEditing((current) =>
                current?.block_start_at === block.block_start_at ? null : block,
              );
            }}
          />
        )}
        </>
        )}
      </main>
    </div>
  );
}
